import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtRatio, STATUS_META, CATEGORY_META, RATING_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info, Pencil, Save, RotateCcw, X, Loader2 } from "lucide-react";

const CAT_ORDER = ["Liquidita", "Solidita", "Redditivita", "Efficienza", "Crisi"];

function RatioCard({ r, companyId, year, onSaved }) {
  const st = STATUS_META[r.status] || STATUS_META.nd;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.giudizio || "");
  const [saving, setSaving] = useState(false);

  const save = async (reset = false) => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/ratio-note`, {
        fiscal_year: year, code: r.code, text: reset ? "" : text,
      });
      toast.success(reset ? "Commento ripristinato all'automatico" : "Commento personalizzato salvato");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error("Errore nel salvataggio del commento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid={`ratio-card-${r.code}`} className="p-4 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium text-muted-foreground leading-snug flex-1">{r.name}</div>
        <div className="flex items-center gap-1 shrink-0">
          {companyId && year && (
            <button data-testid={`ratio-edit-${r.code}`} onClick={() => { setText(r.giudizio || ""); setEditing((v) => !v); }}
              className="text-muted-foreground/60 hover:text-primary" title="Personalizza commento">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid={`ratio-info-${r.code}`} className="text-muted-foreground/60 hover:text-primary">
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px]">
              <p className="font-mono text-xs mb-1">{r.formula}</p>
              <p className="text-xs text-muted-foreground">{r.comment || r.description}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="font-mono font-bold text-2xl mt-2 tracking-tight">{fmtRatio(r.value, r.unit)}</div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
            className="text-xs" placeholder="Commento personalizzato…" data-testid={`ratio-edit-input-${r.code}`} />
          <div className="flex items-center gap-1.5">
            <Button size="sm" className="h-7 px-2 text-xs" onClick={() => save(false)} disabled={saving} data-testid={`ratio-edit-save-${r.code}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => save(true)} disabled={saving} title="Ripristina automatico" data-testid={`ratio-edit-reset-${r.code}`}>
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(false)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ) : (
        r.giudizio && (
          <p data-testid={`ratio-giudizio-${r.code}`} className="text-[11px] leading-snug text-muted-foreground mt-2">
            {r.giudizio_override && <span className="text-primary font-medium">✎ </span>}
            {r.giudizio}
          </p>
        )
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
        <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
          Target {r.benchmark_settore || r.benchmark}
          {r.benchmark_settore && <span className="text-primary/70">· settore</span>}
        </span>
        <Badge variant="outline" className={`text-[10px] font-mono ${st.cls}`}>{st.label}</Badge>
      </div>
    </Card>
  );
}

export function IndiciTab({ analysis, companyId, year, onOverrideSaved }) {
  if (!analysis) return null;
  const cats = {};
  analysis.ratios.forEach((r) => {
    cats[r.category] = cats[r.category] || [];
    cats[r.category].push(r);
  });
  const scoring = analysis.scoring;
  const catSum = {};
  (scoring?.categories || []).forEach((c) => { catSum[c.category] = c; });
  const order = CAT_ORDER.filter((c) => cats[c]).concat(Object.keys(cats).filter((c) => !CAT_ORDER.includes(c)));

  return (
    <TooltipProvider>
      <div className="space-y-8">
        {scoring?.overall && (() => {
          const ov = scoring.overall;
          const meta = RATING_META[ov.letter] || RATING_META.D;
          return (
            <Card data-testid="rating-card" className={`p-5 border ${meta.box}`}>
              <div className="flex items-center gap-5">
                <div className={`font-heading font-bold text-5xl leading-none ${meta.text}`} data-testid="rating-letter">{ov.letter}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-lg tracking-tight">Giudizio Sintetico d'Impresa</h3>
                    <Badge variant="outline" className={`font-mono ${meta.text} ${meta.box}`}>{ov.label}</Badge>
                    <span className="font-mono text-sm text-muted-foreground" data-testid="rating-score">{ov.score}/100</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{ov.summary}</p>
                </div>
              </div>
            </Card>
          );
        })()}

        {order.map((cat) => {
          const cs = catSum[cat];
          return (
            <div key={cat}>
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-heading font-semibold text-lg tracking-tight">
                  {CATEGORY_META[cat]?.label || cat}
                </h3>
                {cs?.score != null && (
                  <Badge variant="outline" className={`text-[10px] font-mono ${(STATUS_META[cs.label === "Solido" ? "ottimo" : cs.label === "Adeguato" ? "adeguato" : cs.label === "Debole" ? "adeguato" : "critico"]).cls}`}>
                    {cs.label} · {cs.score}/100
                  </Badge>
                )}
                <div className="h-px flex-1 bg-border/60" />
              </div>
              {cs?.summary && (
                <p data-testid={`cat-summary-${cat}`} className="text-sm text-muted-foreground mb-4">{cs.summary}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cats[cat].map((r) => (
                  <RatioCard key={r.code} r={r} companyId={companyId} year={year} onSaved={onOverrideSaved} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
